import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  submitCreatorWork,
  type CreatorCenterData,
  type CreatorWork,
  type CreatorDraft,
} from '@/api/member';
import { ensureMemberAccessToken } from '@/utils/memberSession';
import OverviewPanel from './OverviewPanel';
import WorksPanel from './WorksPanel';
import UploadPanel from './UploadPanel';
import PlatformPanel from './PlatformPanel';

type Tab = 'overview' | 'works' | 'upload' | 'platform';

const tabs: { key: Tab; labelKey: string; icon: string }[] = [
  { key: 'overview', labelKey: 'cinema_creatorTabOverview', icon: 'ri-dashboard-3-line' },
  { key: 'works', labelKey: 'cinema_creatorTabWorks', icon: 'ri-movie-2-line' },
  { key: 'upload', labelKey: 'cinema_creatorTabUpload', icon: 'ri-upload-cloud-2-line' },
  { key: 'platform', labelKey: 'cinema_creatorTabPlatform', icon: 'ri-rocket-2-line' },
];

const fallbackAvatar = 'https://readdy.ai/api/search-image?query=Cute%20modern%20anime%20style%20avatar%20portrait%20of%20a%20young%20creative%20filmmaker%20with%20warm%20confident%20expression%20soft%20amber%20and%20rose%20color%20tones%20minimal%20clean%20background%20digital%20illustration%20style%20high%20detail%20gentle%20smile&width=200&height=200&seq=creator-account-01&orientation=squarish';

export default function Studio({ initialData, onDataChange }: { initialData: CreatorCenterData; onDataChange: (data: CreatorCenterData) => void }) {
  const { t } = useTranslation('cinema');
  const [works, setWorks] = useState<CreatorWork[]>(initialData.works || []);
  const [tab, setTab] = useState<Tab>('overview');
  const [toast, setToast] = useState('');
  const [showUploadNotice, setShowUploadNotice] = useState(false);
  const noticeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showUploadNotice) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const buttons = () => Array.from(noticeRef.current?.querySelectorAll<HTMLButtonElement>('button') ?? []);
    buttons()[0]?.focus({ preventScroll: true });
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); setShowUploadNotice(false); }
      if (event.key !== 'Tab') return;
      const items = buttons();
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
      previousFocus?.focus({ preventScroll: true });
    };
  }, [showUploadNotice]);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(''), 2200);
  };

  const handleAdd = async (draft: CreatorDraft) => {
    const token = await ensureMemberAccessToken();
    if (!token) throw new Error('unauthorized');
    const newWork = await submitCreatorWork(draft, token);
    const nextWorks = [newWork, ...works];
    setWorks(nextWorks);
    onDataChange({ ...initialData, works: nextWorks });
    setTab('works');
    showToast(t('cinema_creatorSubmitted'));
  };

  const goUpload = () => {
    setShowUploadNotice(true);
  };

  const confirmUploadNotice = () => {
    setShowUploadNotice(false);
    setTab('upload');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="space-y-5 animate-ao-fade-in-up">
      <div className="relative rounded-2xl overflow-hidden border border-background-800/60">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Cinematic%20abstract%20backdrop%20of%20glowing%20warm%20amber%20rose%20and%20deep%20violet%20light%20streaks%20and%20soft%20bokeh%20over%20dark%20navy%20black%20background%2C%20luxurious%20creator%20studio%20atmosphere%2C%20artistic%20film%20grain%2C%20dreamy%20glow%2C%20premium%20elegant%20high%20contrast%20dramatic%20lighting&width=1200&height=360&seq=creator-studio-bg-1&orientation=landscape"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-tr from-black/85 via-background-950/60 to-background-950/30"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-background-950/80 via-transparent to-transparent"></div>
        <span className="absolute -top-8 right-10 w-32 h-32 rounded-full bg-accent-500/20 blur-3xl pointer-events-none"></span>
        <span className="absolute bottom-0 left-1/4 w-28 h-28 rounded-full bg-amber-400/15 blur-3xl pointer-events-none"></span>

        <div className="relative p-5 md:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex-shrink-0 relative">
              <span className="absolute inset-0 rounded-full bg-gradient-to-tr from-amber-400 via-rose-400 to-accent-400 blur-[2px] scale-105"></span>
              <img
                src={initialData.profile?.avatar || fallbackAvatar}
                alt={initialData.profile?.displayName || ''}
                className="relative w-16 h-16 md:w-20 md:h-20 rounded-full object-cover border-2 border-white/40"
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-base md:text-xl font-bold text-white font-heading truncate">
                  {initialData.profile?.displayName}
                </h2>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold text-background-950 bg-accent-400 whitespace-nowrap">
                  <i className="ri-verified-badge-fill text-[10px] mr-0.5"></i>
                  {t('cinema_creatorLevel')}
                </span>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold text-white/80 bg-white/10 whitespace-nowrap">
                  <i className="ri-copyright-line text-[10px] mr-0.5"></i>
                  {t('cinema_creatorOriginal')}
                </span>
              </div>
              <p className="text-xs text-white/70 mt-1.5">{initialData.profile?.intro}</p>
              <p className="text-[11px] text-white/50 mt-1 flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                {t('cinema_creatorSince')} {initialData.profile?.since?.slice(0, 10)}
              </p>
            </div>
            <button
              type="button"
              onClick={goUpload}
              className="flex-shrink-0 flex items-center gap-1.5 px-5 py-2.5 rounded-full text-xs font-semibold text-background-950 bg-accent-500 hover:bg-accent-400 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-upload-cloud-2-line"></i>
              {t('cinema_creatorAddWork')}
            </button>
          </div>
        </div>
      </div>

      <div className="flex bg-background-900/60 border border-background-800/50 rounded-2xl p-1 overflow-x-auto scrollbar-hide">
        {tabs.map((tb) => (
          <button
            key={tb.key}
            type="button"
            onClick={() => tb.key === 'upload' ? goUpload() : setTab(tb.key)}
            className={`flex-1 flex items-center justify-center gap-1 px-2 sm:px-4 py-2.5 rounded-xl text-xs sm:text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
              tab === tb.key
                ? 'bg-accent-500 text-background-950'
                : 'text-foreground-500 hover:text-foreground-300'
            }`}
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className={tb.icon}></i>
            </span>
            <span className="hidden sm:inline">{t(tb.labelKey)}</span>
            <span className="sm:hidden text-xs">{t(tb.labelKey)}</span>
          </button>
        ))}
      </div>

      <div>
        {tab === 'overview' && <OverviewPanel works={works} />}
        {tab === 'works' && <WorksPanel works={works} onGoUpload={goUpload} />}
        {tab === 'upload' && <UploadPanel onAdd={handleAdd} />}
        {tab === 'platform' && <PlatformPanel />}
      </div>

      {toast && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-lg bg-background-900 border border-background-700 text-sm text-foreground-200 shadow-xl animate-ao-fade-in-up flex items-center gap-2">
          <i className="ri-checkbox-circle-line text-accent-400"></i>
          {toast}
        </div>
      )}

      {showUploadNotice && createPortal(
        <div ref={noticeRef} className="fixed inset-0 z-[80] flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="creator-upload-notice-title">
          <div className="flex max-h-[calc(100dvh-2rem)] w-full max-w-md flex-col overflow-hidden rounded-2xl border border-background-700 bg-background-900 shadow-2xl">
            <div className="flex items-center justify-between border-b border-background-800 px-5 py-4">
              <div className="flex items-center gap-2.5">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent-500/15 text-accent-400"><i className="ri-file-warning-line text-lg" /></span>
                <h3 id="creator-upload-notice-title" className="text-base font-bold text-foreground-50">{t('cinema_creatorUploadNoticeTitle')}</h3>
              </div>
              <button type="button" onClick={() => setShowUploadNotice(false)} className="flex h-8 w-8 items-center justify-center rounded-full text-foreground-500 transition-colors hover:bg-background-800 hover:text-foreground-200" aria-label={t('cinema_cancel')}><i className="ri-close-line text-lg" /></button>
            </div>
            <ol className="min-h-0 space-y-3 overflow-y-auto overscroll-contain px-5 py-5">
              {[1, 2, 3, 4].map((index) => (
                <li key={index} className="flex items-start gap-3 rounded-xl bg-background-800/50 px-4 py-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent-500 text-xs font-bold text-background-950">{index}</span>
                  <p className="pt-0.5 text-sm leading-6 text-foreground-300">{t(`cinema_creatorUploadNotice${index}`)}</p>
                </li>
              ))}
            </ol>
            <div className="flex shrink-0 gap-3 border-t border-background-800 px-5 py-4">
              <button type="button" onClick={() => setShowUploadNotice(false)} className="flex-1 rounded-xl border border-background-700 bg-background-800 py-2.5 text-sm font-semibold text-foreground-300 transition-colors hover:bg-background-700">{t('cinema_cancel')}</button>
              <button type="button" onClick={confirmUploadNotice} className="flex-1 rounded-xl bg-accent-500 py-2.5 text-sm font-semibold text-background-950 transition-colors hover:bg-accent-400">{t('cinema_creatorUploadNoticeConfirm')}</button>
            </div>
          </div>
        </div>, document.body
      )}
    </div>
  );
}
